(() => {
  if (customElements.get('mobius-product-details')) return;

  class MobiusProductDetails extends HTMLElement {
    constructor() {
      super();

      this.raf = null;
      this.currentActiveId = null;
      this.header = null;
      this.mainAddButton = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleDocumentChange = this.handleDocumentChange.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
    }


    /* ----------------------------------------
       LIFECYCLE
       ---------------------------------------- */

    connectedCallback() {
      this.stickyShell = this.querySelector('[data-sticky-shell]');
      this.miniProduct = this.querySelector('[data-mini-product]');
      this.miniPrice = this.querySelector('[data-mini-price]');
      this.miniAddButton = this.querySelector('[data-mini-add]');
      this.nav = this.querySelector('[data-product-nav]');
      this.blocksContainer = this.querySelector('[data-product-blocks]');

      this.headerSelector =
        this.dataset.headerSelector?.trim() || '.section-header';

      this.atcSelector =
        this.dataset.atcSelector?.trim() ||
        "product-info product-form button[name='add']";

      this.priceSelector =
        this.dataset.priceSelector?.trim() || '';

      this.findHeader();
      this.findMainAddButton();

      this.buildNavigation();
      this.updateEverything();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize, { passive: true });

      this.addEventListener('click', this.handleClick);
      document.addEventListener('change', this.handleDocumentChange);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);

      this.setupObservers();

      /*
       Recharge / product scripts sometimes finish updating
       shortly after the DOM change event.
      */
      window.setTimeout(() => this.updateEverything(), 100);
      window.setTimeout(() => this.updateEverything(), 400);
    }


    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('change', this.handleDocumentChange);
      document.removeEventListener(
        'shopify:block:select',
        this.handleBlockSelect
      );

      this.resizeObserver?.disconnect();
      this.blocksObserver?.disconnect();
      this.productObserver?.disconnect();

      if (this.raf) {
        cancelAnimationFrame(this.raf);
      }
    }


    /* ----------------------------------------
       ELEMENT DISCOVERY
       ---------------------------------------- */

    findHeader() {
      try {
        this.header = document.querySelector(this.headerSelector);
      } catch (error) {
        this.header = document.querySelector('.section-header');
      }
    }


    findMainAddButton() {
      /*
       Re-find the element if Dawn / Recharge replaced it.
      */
      if (
        this.mainAddButton &&
        document.documentElement.contains(this.mainAddButton)
      ) {
        return this.mainAddButton;
      }

      let candidates = [];

      try {
        candidates = [...document.querySelectorAll(this.atcSelector)];
      } catch (error) {
        candidates = [];
      }

      /*
       Fallbacks for Dawn-style product forms.
      */
      if (!candidates.length) {
        candidates = [
          ...document.querySelectorAll(
            "product-info product-form button[name='add'], product-form button[name='add']"
          ),
        ];
      }

      /*
       Do not accidentally select anything inside this
       sticky component itself.
      */
      candidates = candidates.filter((button) => !this.contains(button));

      /*
       Prefer a currently rendered button.
      */
      this.mainAddButton =
        candidates.find((button) => button.offsetParent !== null) ||
        candidates[0] ||
        null;

      return this.mainAddButton;
    }


    getDetailBlocks() {
      return [...this.querySelectorAll('[data-mobius-detail]')].filter(
        (block) => block.closest('mobius-product-details') === this
      );
    }


    /* ----------------------------------------
       DYNAMIC NAVIGATION
       ---------------------------------------- */

    buildNavigation() {
      if (!this.nav) return;

      const blocks = this.getDetailBlocks();

      this.nav.innerHTML = '';

      blocks.forEach((block, index) => {
        const id = block.dataset.mobiusDetailId;
        const label =
          block.dataset.menuLabel?.trim() ||
          `Section ${index + 1}`;

        const button = document.createElement('button');

        button.type = 'button';
        button.className = 'mobius-product-nav__button';

        button.dataset.navTarget = id;

        button.textContent = label;

        button.setAttribute(
          'aria-controls',
          `MobiusDetail-${id}`
        );

        this.nav.appendChild(button);
      });

      /*
       Preserve current active block if possible.
       Otherwise activate the first block.
      */
      const currentStillExists = blocks.some(
        (block) =>
          block.dataset.mobiusDetailId === this.currentActiveId
      );

      if (!currentStillExists && blocks.length) {
        this.currentActiveId = blocks[0].dataset.mobiusDetailId;
      }

      this.renderActiveState();
    }


    setActive(id) {
      if (!id || id === this.currentActiveId) return;

      this.currentActiveId = id;
      this.renderActiveState();
    }


    renderActiveState() {
      const buttons =
        this.nav?.querySelectorAll('[data-nav-target]') || [];

      buttons.forEach((button) => {
        const active =
          button.dataset.navTarget === this.currentActiveId;

        button.classList.toggle('is-active', active);

        if (active) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }


    /* ----------------------------------------
       HEADER / STICKY POSITION
       ---------------------------------------- */

    getHeaderOffset() {
      if (
        !this.header ||
        !document.documentElement.contains(this.header)
      ) {
        this.findHeader();
      }

      if (!this.header) return 0;

      const rect = this.header.getBoundingClientRect();

      /*
       The visible bottom edge of the header is what matters.
       This also behaves reasonably if Dawn hides/reveals its
       header while scrolling.
      */
      return Math.max(0, rect.bottom);
    }


    updateHeaderOffset() {
      const offset = this.getHeaderOffset();

      this.style.setProperty(
        '--mobius-header-offset',
        `${offset}px`
      );

      return offset;
    }


    /* ----------------------------------------
       MINI PRODUCT VISIBILITY
       ---------------------------------------- */

    shouldShowMiniProduct(headerOffset) {
      const mainButton = this.findMainAddButton();

      if (!mainButton || !this.stickyShell) return false;

      const buttonRect = mainButton.getBoundingClientRect();
      const sectionRect = this.getBoundingClientRect();

      /*
       Product CTA has passed behind the sticky region.
      */
      const passedMainButton =
        buttonRect.bottom <= headerOffset;

      /*
       Do not show the mini product before this section has
       actually reached the website header.
      */
      const sectionHasReachedHeader =
        sectionRect.top <= headerOffset + 20;

      /*
       Once the entire section has ended, CSS sticky behavior
       naturally pushes the shell upward under the header.
      */
      const sectionStillRelevant =
        sectionRect.bottom > headerOffset;

      return (
        passedMainButton &&
        sectionHasReachedHeader &&
        sectionStillRelevant
      );
    }


    updateMiniVisibility(headerOffset) {
      const shouldShow = this.shouldShowMiniProduct(headerOffset);

      this.classList.toggle(
        'is-mini-visible',
        shouldShow
      );

      if (this.miniProduct) {
        this.miniProduct.setAttribute(
          'aria-hidden',
          shouldShow ? 'false' : 'true'
        );
      }

      this.syncAddButtonState();
      this.syncPrice();
    }


    /* ----------------------------------------
       ADD TO CART
       ---------------------------------------- */

    syncAddButtonState() {
      const originalButton = this.findMainAddButton();

      if (!this.miniAddButton) return;

      if (!originalButton) {
        this.miniAddButton.disabled = true;
        return;
      }

      this.miniAddButton.disabled =
        originalButton.disabled ||
        originalButton.getAttribute('aria-disabled') === 'true';
    }


    triggerMainAddToCart() {
      const originalButton = this.findMainAddButton();

      if (!originalButton || originalButton.disabled) return;

      /*
       Important:
       We click the REAL existing product-page button.

       Recharge / selling-plan / variant fields remain
       controlled by the existing product form.
      */
      originalButton.click();
    }


    /* ----------------------------------------
       PRICE SYNCHRONIZATION
       ---------------------------------------- */

    syncPrice() {
      if (!this.miniPrice) return;

      const livePrice = this.getLivePrice();

      this.miniPrice.textContent =
        livePrice ||
        this.miniPrice.dataset.fallbackPrice ||
        '';
    }


    getLivePrice() {
      /*
       1. Explicit selector supplied in section settings.
      */
      if (this.priceSelector) {
        try {
          const priceElement =
            document.querySelector(this.priceSelector);

          const explicitPrice =
            this.extractMoney(priceElement?.textContent);

          if (explicitPrice) return explicitPrice;
        } catch (error) {
          // Continue to automatic detection.
        }
      }


      /*
       2. Mobius Recharge purchase cards.

       Your current product UI uses .recharge-option, so the
       selected purchase card is the best place to look first.
      */
      const selectedRechargeInput =
        document.querySelector(
          '.recharge-option input[type="radio"]:checked'
        );

      if (selectedRechargeInput) {
        const selectedCard =
          selectedRechargeInput.closest('.recharge-option');

        if (selectedCard) {
          const likelyPriceNode =
            selectedCard.querySelector(
              '[data-price], .price, [class*="price"]'
            );

          const rechargePrice = this.extractMoney(
            likelyPriceNode?.textContent ||
            selectedCard.textContent
          );

          if (rechargePrice) return rechargePrice;
        }
      }


      /*
       3. Dawn product-price fallback.
      */
      const productInfo = document.querySelector('product-info');

      if (productInfo) {
        const priceCandidates = [
          '.price__sale .price-item--sale',
          '.price-item--sale',
          '.price__regular .price-item--regular',
          '.price-item--regular',
        ];

        for (const selector of priceCandidates) {
          const element = productInfo.querySelector(selector);

          if (!element) continue;

          const styles = window.getComputedStyle(element);

          if (
            styles.display === 'none' ||
            styles.visibility === 'hidden'
          ) {
            continue;
          }

          const price = this.extractMoney(element.textContent);

          if (price) return price;
        }
      }

      return null;
    }


    extractMoney(text) {
      if (!text) return null;

      /*
       Handles values such as:
       $29
       $29.00
       €29
       £29.00
       USD 29.00
      */
      const match = text.match(
        /(?:USD\s*)?[$€£]\s?\d[\d,]*(?:\.\d{1,2})?|USD\s*\d[\d,]*(?:\.\d{1,2})?/i
      );

      return match ? match[0].trim() : null;
    }


    /* ----------------------------------------
       SCROLL SPY
       ---------------------------------------- */

    updateActiveFromScroll(headerOffset) {
      const blocks = this.getDetailBlocks();

      if (!blocks.length || !this.stickyShell) return;

      const stickyHeight =
        this.stickyShell.getBoundingClientRect().height;

      /*
       Think of this as an invisible horizontal line directly
       below the sticky product UI.

       Whichever content section occupies this line becomes active.
      */
      const activationLine =
        headerOffset + stickyHeight + 18;

      let activeBlock = blocks[0];

      for (const block of blocks) {
        const rect = block.getBoundingClientRect();

        if (rect.top <= activationLine) {
          activeBlock = block;
        }

        if (
          rect.top <= activationLine &&
          rect.bottom > activationLine
        ) {
          activeBlock = block;
          break;
        }
      }

      this.setActive(
        activeBlock.dataset.mobiusDetailId
      );
    }


    /* ----------------------------------------
       CLICK-TO-SCROLL
       ---------------------------------------- */

    scrollToDetail(id) {
  const target = this.querySelector(
    `[data-mobius-detail-id="${CSS.escape(id)}"]`
  );

  if (!target) return;

  this.setActive(id);

  const headerOffset = this.updateHeaderOffset();

  const nav =
    this.querySelector('.mobius-product-nav');

  const navHeight =
    nav?.getBoundingClientRect().height || 0;

  const miniExpandedHeight =
    this.getExpandedMiniHeight();

  const miniIsAlreadyVisible =
    this.classList.contains('is-mini-visible');

  const targetDocumentTop =
    window.scrollY +
    target.getBoundingClientRect().top;

  /*
    Important:

    If the mini product viewer is already visible, its height
    is already part of the document layout, so subtract it.

    If it is currently collapsed and opens during this scroll,
    its expansion will push the target down by the same amount.
    Therefore we intentionally do NOT subtract its future height.
  */
  const totalStickyHeight =
    navHeight +
    (miniIsAlreadyVisible ? miniExpandedHeight : 0);

  const destination =
    targetDocumentTop -
    headerOffset -
    totalStickyHeight -
    12;

  const reduceMotion =
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

  window.scrollTo({
    top: Math.max(0, destination),
    behavior: reduceMotion ? 'auto' : 'smooth',
  });
}


    getExpandedMiniHeight() {
      if (!this.miniProduct) return 0;

      const wasVisible =
        this.classList.contains('is-mini-visible');

      /*
       scrollHeight is available even while our grid row is
       visually collapsed.
      */
      const height =
        this.miniProduct.scrollHeight || 0;

      if (!wasVisible) {
        return height;
      }

      return this.miniProduct.getBoundingClientRect().height;
    }


    /* ----------------------------------------
       OBSERVERS
       ---------------------------------------- */

    setupObservers() {
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.scheduleUpdate();
        });

        if (this.header) {
          this.resizeObserver.observe(this.header);
        }

        if (this.stickyShell) {
          this.resizeObserver.observe(this.stickyShell);
        }
      }


      /*
       Rebuild navigation automatically when a Theme Block is
       added, removed, or reordered in the Shopify editor.
      */
      if (this.blocksContainer) {
        this.blocksObserver = new MutationObserver(() => {
          this.buildNavigation();
          this.scheduleUpdate();
        });

        this.blocksObserver.observe(
          this.blocksContainer,
          {
            childList: true,
          }
        );
      }


      /*
       Watch the main product interface for Recharge / price /
       availability changes.
      */
      const productInfo =
        document.querySelector('product-info');

      if (productInfo) {
        this.productObserver =
          new MutationObserver(() => {
            this.findMainAddButton();
            this.syncAddButtonState();
            this.syncPrice();
          });

        this.productObserver.observe(
          productInfo,
          {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
          }
        );
      }


      /*
       Dawn's sticky header can animate between visible and
       hidden states.
      */
      if (this.header) {
        this.header.addEventListener(
          'transitionend',
          () => this.scheduleUpdate()
        );
      }
    }


    /* ----------------------------------------
       EVENTS
       ---------------------------------------- */

    handleClick(event) {
      const navButton =
        event.target.closest('[data-nav-target]');

      if (navButton && this.contains(navButton)) {
        this.scrollToDetail(
          navButton.dataset.navTarget
        );

        return;
      }


      const miniAdd =
        event.target.closest('[data-mini-add]');

      if (miniAdd && this.contains(miniAdd)) {
        this.triggerMainAddToCart();
      }
    }


    handleDocumentChange() {
      /*
       Allows Recharge radio changes, variants, etc.
       time to finish updating before we read them.
      */
      window.setTimeout(() => {
        this.findMainAddButton();
        this.syncAddButtonState();
        this.syncPrice();
        this.scheduleUpdate();
      }, 0);

      window.setTimeout(() => {
        this.syncPrice();
      }, 150);
    }


    handleBlockSelect(event) {
      const selectedBlock =
        event.target.closest?.('[data-mobius-detail]');

      if (
        !selectedBlock ||
        selectedBlock.closest('mobius-product-details') !== this
      ) {
        return;
      }

      const id =
        selectedBlock.dataset.mobiusDetailId;

      if (id) {
        this.scrollToDetail(id);
      }
    }


    handleScroll() {
      this.scheduleUpdate();
    }


    handleResize() {
      this.scheduleUpdate();
    }


    /* ----------------------------------------
       FRAME UPDATE
       ---------------------------------------- */

    scheduleUpdate() {
      if (this.raf) return;

      this.raf = requestAnimationFrame(() => {
        this.raf = null;
        this.updateEverything();
      });
    }


    updateEverything() {
      const headerOffset =
        this.updateHeaderOffset();

      this.updateMiniVisibility(headerOffset);
      this.updateActiveFromScroll(headerOffset);
    }
  }


  customElements.define(
    'mobius-product-details',
    MobiusProductDetails
  );
})();